import React from "react";
import { AlertTriangle } from "lucide-react";

/**
 * ErrorBoundary — friendly full-page fallback for React runtime crashes.
 *
 * Wraps public-facing routes (InvoicePay, PublicProfile) so a future
 * ReferenceError / TypeError / null-deref in a deeply-nested prop
 * never blanks the whole screen. The visitor gets a clear
 * "something broke, click retry" state and — critically — the
 * merchant's brand stays visible instead of a white void.
 *
 * Why a class component in a fully-hooks codebase?
 *   `componentDidCatch` and `getDerivedStateFromError` only exist on
 *   class components; React's `useErrorBoundary` proposal isn't in
 *   stable yet. This is the ONLY class component in the repo,
 *   quarantined here.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }

  static getDerivedStateFromError(err) {
    return { err };
  }

  componentDidCatch(err, info) {
    // Best-effort log to console — real observability hooks go here
    // (Sentry, etc.) if we wire them in later.
     
    console.error("ErrorBoundary caught:", err, info?.componentStack);
  }

  handleRetry = () => {
    this.setState({ err: null });
  };

  render() {
    if (!this.state.err) return this.props.children;

    return (
      <main
        data-testid="error-boundary-fallback"
        className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center px-6"
      >
        <div className="max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-500/10 text-amber-400 mb-5">
            <AlertTriangle size={22} />
          </div>
          <h1 className="text-xl font-bold tracking-tight mb-2">
            Something broke on this page.
          </h1>
          <p className="mono text-[12px] text-zinc-500 mb-6">
            no funds were touched — crypt.pe never holds your money.
            <br />try reloading, or head back to the profile.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              data-testid="error-boundary-retry"
              type="button"
              onClick={this.handleRetry}
              className="mono text-[12px] px-4 py-2 rounded-md bg-mint text-black hover:opacity-90"
            >
              retry
            </button>
            <a
              data-testid="error-boundary-home"
              href="/"
              className="mono text-[12px] px-4 py-2 rounded-md border border-zinc-800 text-zinc-300 hover:text-white"
            >
              go home
            </a>
          </div>
        </div>
      </main>
    );
  }
}
